import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { X, AlertTriangle, Gift, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ExitIntentPopup() {
  const [isVisible, setIsVisible] = useState(false);
  const [formData, setFormData] = useState({ name: "", email: "", phone: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    // Check if already shown or claimed
    const hasShownExit = localStorage.getItem("exitIntent_shown");
    if (hasShownExit) return;

    const handleMouseLeave = (e) => {
      // Only trigger when mouse leaves at the top of the page
      if (e.clientY <= 5 && !isVisible) {
        setIsVisible(true);
        localStorage.setItem("exitIntent_shown", "true");
      }
    };

    // Add event listener after a delay (don't show immediately)
    const timer = setTimeout(() => {
      document.addEventListener("mouseleave", handleMouseLeave);
    }, 10000); // Wait 10 seconds before enabling exit intent

    return () => {
      clearTimeout(timer);
      document.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, [isVisible]);

  const handleClose = () => {
    setIsVisible(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!formData.name || !formData.email || !formData.phone) {
      setError("Please fill in all fields");
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/exit-intent/claim`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        offer: "15% Off - Exit Intent",
        discount: "15%"
      });

      localStorage.setItem("exitIntent_claimed", "true");
      setShowSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isVisible) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/85 backdrop-blur-sm"
        onClick={handleClose}
      >
        <motion.div
          initial={{ scale: 0.8, opacity: 0, y: -50 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.8, opacity: 0, y: -50 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="relative max-w-md w-full overflow-hidden rounded-3xl shadow-2xl bg-dream-surface border border-dream-border"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close button */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 z-20 text-white/60 hover:text-white transition-all"
          >
            <X className="w-6 h-6" />
          </button>

          {!showSuccess ? (
            <div className="p-8">
              {/* Warning Icon */}
              <div className="text-center mb-6">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", delay: 0.2 }}
                  className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4"
                >
                  <AlertTriangle className="w-8 h-8 text-red-500" />
                </motion.div>
                
                <h2 className="font-heading text-2xl font-bold text-white mb-2">
                  Wait! Don't Leave Empty-Handed!
                </h2>
                <p className="text-white/60 text-sm">
                  We'd hate to see you go without a special offer...
                </p>
              </div>

              {/* Offer Box */}
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="bg-gradient-to-r from-red-500 to-orange-500 text-white p-6 rounded-2xl text-center mb-6"
              >
                <p className="text-sm font-medium mb-1">EXCLUSIVE EXIT OFFER</p>
                <p className="text-4xl font-bold mb-1">15% OFF</p>
                <p className="text-sm opacity-90">Your First Session</p>
              </motion.div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <Input
                  type="text"
                  placeholder="Your Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="bg-dream-bg border-dream-border text-white placeholder:text-white/40"
                  required
                />
                <Input
                  type="email"
                  placeholder="Email Address"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="bg-dream-bg border-dream-border text-white placeholder:text-white/40"
                  required
                />
                <Input
                  type="tel"
                  placeholder="Mobile Number"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="bg-dream-bg border-dream-border text-white placeholder:text-white/40"
                  required
                />

                {error && (
                  <p className="text-red-400 text-sm text-center">{error}</p>
                )}

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 py-6 font-bold text-lg rounded-xl"
                >
                  {isSubmitting ? "Claiming..." : "Claim 15% Off Now!"}
                </Button>
              </form>

              <button
                onClick={handleClose}
                className="w-full text-white/40 text-sm mt-4 hover:text-white/60 transition-colors"
              >
                No thanks, I'll pay full price
              </button>
            </div>
          ) : (
            /* Success State */
            <div className="p-8 text-center">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring" }}
                className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6"
              >
                <Gift className="w-10 h-10 text-green-500" />
              </motion.div>

              <h3 className="font-heading text-2xl font-bold text-white mb-3">
                Great Choice!
              </h3>
              <p className="text-white/60 mb-6">
                Your 15% discount has been saved! We'll be in touch to help you book your session.
              </p>

              <Link to="/booking">
                <Button
                  onClick={handleClose}
                  className="w-full bg-dream-violet hover:bg-dream-violet-glow py-6 rounded-xl"
                >
                  Book Now With Discount
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
